import { useState } from "react";
import { Button, Dialog, ErrorSummary, PortalPanelBody, RadioGroup, Stack, TextField, Typography } from "../../design-system";
import type { ErrorSummaryItem } from "../../design-system";
import { reassignAssignment, takeBackAssignment } from "../../portal-auth";
import type { AssignmentCreated, TakeBackResult } from "../../portal-auth";
import { isRenewalSubmittedWriteRefusal } from "../../state/outcome";
import { getErrorMessage } from "../../validation/error-registry";
import { trimAssigneeName, validateAssigneeFields } from "./assignee-fields";
import type { AssigneeFieldErrors } from "./assignee-fields";
import type { AssignDialogSection } from "./AssignDialog";
import { renewalCaptureContent as content } from "./content";

export interface ReassignDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly currentSectionAssigneeId: number;
  readonly currentAssigneeName: string;
  readonly currentAssigneeEmail: string;
  readonly section: AssignDialogSection;
  readonly onReassigned: (result: AssignmentCreated, assigneeName: string, assigneeEmail: string) => void;
  readonly onTakenBack: (result: TakeBackResult) => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-a7zv): the server refused the
   * reassign/take-back with 409 `renewal_submitted` -- the caller routes
   * the session to `/renewal-status` instead of this dialog's own generic
   * server-error panel. */
  readonly onRenewalSubmitted?: () => void;
}

type ReassignChoice = "someone-else" | "yourself";

const CHOICE_FIELD_ID = "reassign-dialog-choice";
const NAME_FIELD_ID = "reassign-dialog-name";
const EMAIL_FIELD_ID = "reassign-dialog-email";

/**
 * TG-6, AC-17: "Assign it to someone else" (TG-2's reassign route) or
 * "Complete it yourself" (TG-2's take-back route). The caller shows the
 * H2 status panel after either succeeds.
 */
export function ReassignDialog({
  open,
  onClose,
  currentSectionAssigneeId,
  currentAssigneeName,
  currentAssigneeEmail,
  section,
  onReassigned,
  onTakenBack,
  onRenewalSubmitted,
}: Readonly<ReassignDialogProps>) {
  const [choice, setChoice] = useState<ReassignChoice | "">("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [choiceError, setChoiceError] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<AssigneeFieldErrors | null>(null);
  const [conflictError, setConflictError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);

  function clearErrors() {
    setChoiceError(undefined);
    setFieldErrors(null);
    setConflictError(undefined);
    setServerError(undefined);
  }

  async function takeBack() {
    setIsSubmitting(true);
    const result = await takeBackAssignment({ sectionAssigneeId: currentSectionAssigneeId, sectionIds: [section.sectionId] });
    setIsSubmitting(false);
    if (!result.ok) {
      if (isRenewalSubmittedWriteRefusal(result.error.code) && onRenewalSubmitted !== undefined) {
        onRenewalSubmitted();
        return;
      }
      setServerError(content.serverErrorMessage);
      return;
    }
    onTakenBack(result.value);
  }

  async function reassign() {
    const validation = validateAssigneeFields(name, email, NAME_FIELD_ID, EMAIL_FIELD_ID);
    if (validation.summary.length > 0) {
      setFieldErrors(validation);
      return;
    }
    setIsSubmitting(true);
    const result = await reassignAssignment({
      currentSectionAssigneeId,
      assigneeName: trimAssigneeName(name),
      assigneeEmail: email.trim(),
      sectionIds: [section.sectionId],
    });
    setIsSubmitting(false);
    if (!result.ok) {
      if (result.error.code === "assignee_unchanged") {
        setConflictError(getErrorMessage("assign.samePerson", "conflict"));
        return;
      }
      if (isRenewalSubmittedWriteRefusal(result.error.code) && onRenewalSubmitted !== undefined) {
        onRenewalSubmitted();
        return;
      }
      setServerError(content.serverErrorMessage);
      return;
    }
    onReassigned(result.value, trimAssigneeName(name), email.trim());
  }

  async function handleConfirm() {
    clearErrors();
    if (choice === "") {
      setChoiceError(getErrorMessage("reassign.choice", "missing"));
      return;
    }
    if (choice === "yourself") {
      await takeBack();
      return;
    }
    await reassign();
  }

  const summaryItems: ErrorSummaryItem[] = [
    ...(choiceError !== undefined ? [{ fieldId: CHOICE_FIELD_ID, text: choiceError }] : []),
    ...(fieldErrors?.summary ?? []),
    ...(conflictError !== undefined ? [{ fieldId: NAME_FIELD_ID, text: conflictError }] : []),
  ];

  let nameErrorText: string | undefined;
  if (fieldErrors?.nameError !== undefined) {
    nameErrorText = fieldErrors.nameError;
  } else if (conflictError !== undefined) {
    nameErrorText = conflictError;
  }
  const nameFieldProps = nameErrorText !== undefined ? { errorText: nameErrorText } : {};

  const emailErrorText = fieldErrors?.emailError;
  const emailFieldProps = emailErrorText !== undefined ? { errorText: emailErrorText } : {};

  return (
    <Dialog open={open} title={content.reassignDialogTitle} onClose={onClose} testId="reassign-dialog">
      <Stack gap="sm">
        <Typography variant="body">
          {content.reassignDialogIntroPrefix}
          <strong>{section.title}</strong>
          {content.reassignDialogIntroJoin}
          {currentAssigneeName}
          {content.reassignDialogIntroEmailOpen}
          {currentAssigneeEmail}
          {content.reassignDialogIntroEmailClose}
        </Typography>
        {summaryItems.length > 0 ? (
          <ErrorSummary items={summaryItems} id="reassign-dialog-errors" testId="reassign-dialog-errors" />
        ) : null}
        {serverError !== undefined ? (
          <PortalPanelBody id="reassign-dialog-server-error" expanded tone="error" title={content.serverErrorTitle} testId="reassign-dialog-server-error">
            <Typography variant="body">{serverError}</Typography>
          </PortalPanelBody>
        ) : null}
        <RadioGroup
          id={CHOICE_FIELD_ID}
          legend={content.reassignChoiceLegend}
          legendVisuallyHidden
          variant="plain"
          value={choice}
          onChange={(value) => {
            setChoice(value as ReassignChoice);
            clearErrors();
          }}
          options={[
            { value: "someone-else", label: content.reassignOptionSomeoneElse, help: content.reassignOptionSomeoneElseHelp },
            { value: "yourself", label: content.reassignOptionYourself, help: content.reassignOptionYourselfHelp },
          ]}
          error={choiceError !== undefined}
          disabled={isSubmitting}
        />
        {choice === "someone-else" ? (
          <>
            <TextField
              id={NAME_FIELD_ID}
              label={content.theirNameLabel}
              value={name}
              onChange={(value) => {
                setName(value);
                clearErrors();
              }}
              autoComplete="off"
              error={fieldErrors?.nameError !== undefined || conflictError !== undefined}
              disabled={isSubmitting}
              {...nameFieldProps}
            />
            <TextField
              id={EMAIL_FIELD_ID}
              label={content.theirEmailLabel}
              value={email}
              onChange={(value) => {
                setEmail(value);
                clearErrors();
              }}
              type="email"
              autoComplete="off"
              error={fieldErrors?.emailError !== undefined}
              disabled={isSubmitting}
              {...emailFieldProps}
            />
          </>
        ) : null}
        <Stack direction="dialogActions" gap="sm">
          <Button emphasis="primary" onClick={() => void handleConfirm()} disabled={isSubmitting} testId="reassign-dialog-submit">
            {content.reassignDialogConfirmLabel}
          </Button>
          <Button emphasis="quiet" onClick={onClose} disabled={isSubmitting} testId="reassign-dialog-cancel">
            {content.dialogCancelLabel}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
